import type { ActivateAccountRequest } from '../models/activate-account-request';
import type { CurrentSession } from '../models/current-session';
import type { RegisterRequest } from '../models/register-request';
import type { SignInRequest } from '../models/sign-in-request';
import { forgetPushSubscription } from '../push/browser-push';
import { clearSession, setSession } from '../session/session-store';
import { apiGet, apiPost } from './api-client';

/**
 * The customer half of authentication. Every route here is under `/auth/customer`: staff sign in
 * through `/auth/staff` and get a session in a different audience under a differently named cookie,
 * which is what keeps a portal session from ever reaching a staff endpoint.
 */
export async function signIn(request: SignInRequest): Promise<void> {
    const result = await apiPost<CurrentSession>('/auth/customer/sign-in', { body: request });
    setSession(result);
}

/**
 * Creates an account from the public site, and <b>is</b> signed in when it returns — the server
 * opens the session, so this stores it exactly as `signIn` does.
 *
 * That differs from `activateAccount` on purpose. An invitation is redeemed by whoever holds the
 * link, so redeeming one deliberately yields no session; here the person at the form chose the
 * password a moment ago, and asking them to type it again immediately proves nothing.
 */
export async function register(request: RegisterRequest): Promise<void> {
    const result = await apiPost<CurrentSession>('/auth/customer/register', { body: request });
    setSession(result);
}

/**
 * Redeems an invitation and sets the password. Deliberately does not sign the customer in: the
 * server opens no session, so a stolen link on its own never yields one.
 */
export async function activateAccount(request: ActivateAccountRequest): Promise<void> {
    await apiPost<void>('/auth/customer/activate', { body: request });
}

/**
 * Confirms with the server, once per page load, whether the cookie in this browser is still a
 * session — and refreshes the cached account while it is at it.
 *
 * It runs even when nothing is cached locally, because the cookie is the truth and the cache is
 * not: a customer who cleared their site data in one tab, or arrived on a fresh tab after signing
 * in elsewhere, still has a live session the portal should honour. It runs when something *is*
 * cached too, because a session can have been ended server-side since — by idleness, a password
 * change, or the connection moving network — and a stored expiry says nothing about that.
 *
 * Only the portal routes await it, so a visitor reading an article never waits on an authentication
 * round trip and the prerendered pages need no session at all.
 */
let probe: Promise<boolean> | null = null;

export function ensureSessionLoaded(): Promise<boolean> {
    probe ??= loadSession();

    return probe;
}

async function loadSession(): Promise<boolean> {
    try {
        setSession(await apiGet<CurrentSession>('/auth/customer/session', { authenticated: true }));
        return true;
    } catch {
        // Any failure here means "not signed in as far as this page is concerned". A network
        // failure lands in the same branch, and the portal routes will send the customer to
        // sign-in — which is the right answer when nothing can be loaded anyway.
        clearSession();
        return false;
    }
}

export async function signOut(allDevices = false): Promise<void> {
    try {
        // Before the session ends, because deleting the row needs one: a shared telephone must not
        // keep receiving case numbers for somebody who has left.
        await forgetPushSubscription();

        await apiPost<void>('/auth/customer/sign-out', { authenticated: true, body: { allDevices } });
    } finally {
        // Local state is cleared even if the call failed: the customer asked to be signed out, and
        // leaving them apparently signed in would be worse than a session row that outlives the
        // browser's cookie. The probe is forgotten too, so signing in again is confirmed against
        // the server rather than answered from this page load's first result.
        clearSession();

        // Forgotten too, so signing in again is confirmed against the server rather than answered
        // from this page load's first result.
        probe = null;
    }
}
